/**
 * Tax Position Centre — individual client workspace (Phase 1).
 *
 * Left sidebar: 4-step workflow (GST / PAYE / Income Tax / Review).
 * Centre: active-step form (Phase 1 ships PAYE; GST & Income Tax are
 * stubs with "coming next phase" copy).
 * Right: live Client Report Preview that renders the plain-English
 * "Your Tax Position" view the client will receive.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft, Check, AlertCircle, History, Save, ExternalLink, Lightbulb,
    MoreVertical, UserSquare2, Users, Calendar as CalendarIcon, ClipboardPaste,
    Send, X, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const nz = (v) => (v == null ? "—" : `NZ$${Number(v).toLocaleString("en-NZ", { maximumFractionDigits: 0 })}`);

const STEPS = [
    { key: "gst",        label: "GST",          hint: "Enter current GST position" },
    { key: "paye",       label: "PAYE / Payroll", hint: "Enter PAYE details" },
    { key: "income_tax", label: "Income Tax",   hint: "Enter income tax & provisional tax" },
    { key: "review",     label: "Review & Save", hint: "Confirm details" },
];

export default function TaxPositionClient() {
    const { clientId } = useParams();
    const [doc, setDoc] = useState(null);
    const [step, setStep] = useState("paye");
    const [view, setView] = useState("staff");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [sendOpen, setSendOpen] = useState(false);
    const [pasteOpen, setPasteOpen] = useState(false);

    async function load() {
        try {
            setLoading(true);
            const { data } = await api.get(`/tax-position/clients/${clientId}`);
            setDoc(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load client");
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

    async function patch(patch, { silent = false } = {}) {
        try {
            setSaving(true);
            const { data } = await api.patch(`/tax-position/clients/${clientId}`, patch);
            setDoc((d) => ({ ...d, state: data.state, totals: data.totals, stale: data.stale, days_since_check: data.days_since_check }));
            if (!silent) toast.success("Saved");
            return data;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    }

    async function finalise() {
        try {
            setSaving(true);
            await api.post(`/tax-position/clients/${clientId}/finalise`);
            toast.success("Snapshot saved");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Finalise failed");
        } finally { setSaving(false); }
    }

    if (loading || !doc) {
        return <div className="p-10 text-[13px] text-[#5E636E]">Loading tax position…</div>;
    }
    const client = doc.client;
    const state = doc.state;
    const totals = doc.totals || {};
    const paye = state.paye || {};

    const stepStatus = (k) => {
        if (k === "gst")        return state.gst?.verified_at || state.gst?.verified_zero ? "done" : "pending";
        if (k === "paye")       return state.paye?.verified_at || state.paye?.verified_zero ? "done" : (step === "paye" ? "active" : "pending");
        if (k === "income_tax") return state.income_tax?.verified_at || state.income_tax?.verified_zero ? "done" : "pending";
        if (k === "review")     return "pending";
    };

    return (
        <div className="p-6 md:p-8 space-y-5" data-testid="tax-position-client">
            <div className="flex items-center justify-between">
                <Link to="/app/tax-position" className="inline-flex items-center gap-1.5 text-[12.5px] text-[#5E636E] hover:text-[#0F1115]"
                    data-testid="back-to-clients">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to clients
                </Link>
                <div className="flex items-center gap-2">
                    <Button variant="outline" className="h-8 gap-1.5 text-[12.5px]" onClick={() => setPasteOpen(true)}
                        data-testid="paste-myir-btn">
                        <ClipboardPaste className="h-3.5 w-3.5" /> Paste from myIR
                    </Button>
                    <Button variant="outline" className="h-8 gap-1.5 text-[12.5px]" data-testid="view-history-btn">
                        <History className="h-3.5 w-3.5" /> View History
                    </Button>
                    <Button className="h-8 gap-1.5 text-[12.5px]" onClick={finalise} disabled={saving}
                        data-testid="save-updates-btn">
                        <Save className="h-3.5 w-3.5" /> Save Updates
                    </Button>
                </div>
            </div>

            <header className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-[24px] font-bold text-[#0F1115] tracking-tight">{client.name}</h1>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-[#5E636E] mt-1">
                        {client.ird_number && <span>IRD {client.ird_number}</span>}
                        {client.gst_registered && <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> GST Registered ({client.gst_frequency === "two_monthly" ? "2 monthly" : (client.gst_frequency || "monthly")})</span>}
                        {client.entity_type && <span className="capitalize">{client.entity_type}</span>}
                        <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Xero Connected</span>
                    </div>
                    <div className="text-[11.5px] text-[#5E636E] mt-1">
                        Last IRD check: {state.last_ird_check_at ? `${new Date(state.last_ird_check_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })} by ${state.last_ird_check_by_name || "—"}` : "Not checked yet"}
                    </div>
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5">
                {/* Main workflow area */}
                <section className="space-y-4">
                    <nav className="flex items-center gap-5 border-b border-[#E4E4E7] text-[13px]">
                        {["IRD Tax Accounts", "Summary", "History", "Notes", "Documents", "Client Details"].map((t, i) => (
                            <button key={t} className={`py-2 ${i === 0 ? "font-semibold text-[#0F1115] border-b-2 border-[#0F1115]" : "text-[#5E636E] hover:text-[#0F1115]"}`}
                                data-testid={`tab-${t.replace(/\W+/g, '-').toLowerCase()}`}>
                                {t}
                            </button>
                        ))}
                    </nav>

                    <div className="grid grid-cols-1 lg:grid-cols-[200px_minmax(0,1fr)_260px] gap-4">
                        {/* 4-step sidebar */}
                        <aside className="rounded-xl border border-[#E4E4E7] bg-white p-3 self-start">
                            <div className="text-[11px] uppercase tracking-wider text-[#5E636E] px-1 pb-2">Update IRD information</div>
                            <ul className="space-y-1">
                                {STEPS.map((s, i) => {
                                    const st = stepStatus(s.key);
                                    const active = step === s.key;
                                    return (
                                        <li key={s.key}>
                                            <button onClick={() => setStep(s.key)}
                                                className={`w-full flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-left ${active ? "bg-zinc-50 ring-1 ring-[#E4E4E7]" : "hover:bg-zinc-50"}`}
                                                data-testid={`step-${s.key}`}>
                                                <span className={`mt-0.5 h-5 w-5 shrink-0 rounded-full flex items-center justify-center text-[11px] font-semibold ${st === "done" ? "bg-emerald-500 text-white" : active ? "bg-[#0F1115] text-white" : "bg-zinc-200 text-[#5E636E]"}`}>
                                                    {st === "done" ? <Check className="h-3 w-3" /> : (i + 1)}
                                                </span>
                                                <span>
                                                    <div className={`text-[13px] ${active ? "font-semibold text-[#0F1115]" : "text-[#0F1115]"}`}>{s.label}</div>
                                                    <div className="text-[11.5px] text-[#5E636E]">{s.hint}</div>
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </aside>

                        {/* Step form */}
                        {step === "paye" ? (
                            <PayeStep paye={paye} saving={saving} onPatch={(block) => patch({ paye: block })} />
                        ) : step === "gst" ? (
                            <GstStep gst={state.gst || {}} saving={saving} onPatch={(block) => patch({ gst: block })} />
                        ) : step === "income_tax" ? (
                            <IncomeTaxStep it={state.income_tax || {}} saving={saving} onPatch={(block) => patch({ income_tax: block })} />
                        ) : (
                            <ReviewStep totals={totals} client={client} onFinalise={finalise} saving={saving} />
                        )}

                        {/* Right sidekick summary varies per step */}
                        {step === "paye" && <PayeSummary paye={paye} totals={totals} />}
                        {step === "gst" && <GstSummary gst={state.gst || {}} />}
                        {step === "income_tax" && <IncomeTaxSummary it={state.income_tax || {}} />}
                    </div>
                </section>

                {/* Right rail: Client Report Preview + Staff/Client view switch */}
                <aside className="space-y-3">
                    <div className="flex items-center gap-2">
                        <div className="inline-flex rounded-md border border-[#E4E4E7] bg-white p-0.5">
                            <button onClick={() => setView("staff")}
                                className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-[12px] ${view === "staff" ? "bg-[#0F1115] text-white" : "text-[#5E636E]"}`}
                                data-testid="switch-staff-view">
                                <UserSquare2 className="h-3.5 w-3.5" /> Staff View
                            </button>
                            <button onClick={() => setView("client")}
                                className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-[12px] ${view === "client" ? "bg-[#0F1115] text-white" : "text-[#5E636E]"}`}
                                data-testid="switch-client-view">
                                <Users className="h-3.5 w-3.5" /> Client View
                            </button>
                        </div>
                    </div>
                    <ClientReportPreview totals={totals} client={client} onSend={() => setSendOpen(true)} />
                </aside>
            </div>

            <SendReportDialog open={sendOpen} onOpenChange={setSendOpen}
                clientId={clientId} clientName={client.name} totals={totals} />
            <PasteMyirDialog open={pasteOpen} onOpenChange={setPasteOpen}
                onApply={async (parsed) => {
                    if (parsed.gst && Object.keys(parsed.gst).length) {
                        await patch({ gst: { ...state.gst, ...parsed.gst, verified_at: new Date().toISOString(), source: "myir_verified" } }, { silent: true });
                    }
                    if (parsed.paye && Object.keys(parsed.paye).length) {
                        await patch({ paye: { ...state.paye, ...parsed.paye, verified_at: new Date().toISOString(), source: "myir_verified" } }, { silent: true });
                    }
                    if (parsed.income_tax && Object.keys(parsed.income_tax).length) {
                        const existingInsts = Array.isArray(state.income_tax?.provisional_instalments) ? state.income_tax.provisional_instalments : [];
                        const addedInsts = parsed.income_tax.provisional_instalments || [];
                        const next = { ...state.income_tax, ...parsed.income_tax };
                        if (addedInsts.length) next.provisional_instalments = [...existingInsts, ...addedInsts];
                        await patch({ income_tax: { ...next, verified_at: new Date().toISOString(), source: "myir_verified" } }, { silent: true });
                    }
                    await patch({ mark_checked_now: true }, { silent: true });
                    toast.success("Fields populated from myIR paste");
                }} />
        </div>
    );
}

function PayeStep({ paye, saving, onPatch }) {
    const [draft, setDraft] = useState(paye);
    useEffect(() => { setDraft(paye); }, [paye]);

    function setField(k, v) { setDraft((d) => ({ ...d, [k]: v })); }

    async function save() {
        await onPatch({
            ...draft,
            balance: draft.balance === "" || draft.balance == null ? null : Number(draft.balance),
            overdue: draft.overdue === "" || draft.overdue == null ? null : Number(draft.overdue),
            current_not_due: draft.current_not_due === "" || draft.current_not_due == null ? null : Number(draft.current_not_due),
            verified_at: new Date().toISOString(),
        });
    }

    return (
        <section className="rounded-xl border border-[#E4E4E7] bg-white p-5" data-testid="paye-step">
            <div className="flex items-start justify-between mb-1">
                <div>
                    <h2 className="text-[15px] font-semibold text-[#0F1115]">PAYE / Payroll</h2>
                    <p className="text-[12.5px] text-[#5E636E] mt-0.5">Enter the current IRD PAYE balance and upcoming PAYE liabilities. Include any previous PAYE not yet paid.</p>
                </div>
                <a href="https://www.ird.govt.nz/" target="_blank" rel="noreferrer"
                    className="shrink-0 inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white px-2.5 py-1 text-[11.5px] text-[#5E636E] hover:bg-zinc-50 whitespace-nowrap">
                    <Lightbulb className="h-3 w-3" /> myIR help <ExternalLink className="h-3 w-3" />
                </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                <Field label="Current IRD PAYE account balance *" required testid="paye-balance">
                    <CurrencyInput value={draft.balance} onChange={(v) => setField("balance", v)} />
                    <label className="flex items-center gap-2 mt-2 text-[12.5px] text-[#5E636E]">
                        <input type="checkbox" checked={!!draft.includes_overdue}
                            onChange={(e) => setField("includes_overdue", e.target.checked)} />
                        Includes overdue amount
                    </label>
                </Field>
                <Field label="Overdue portion (if known) *" required testid="paye-overdue">
                    <CurrencyInput value={draft.overdue} onChange={(v) => setField("overdue", v)} />
                </Field>
                <Field label="Current PAYE not yet due *" required testid="paye-current-not-due">
                    <CurrencyInput value={draft.current_not_due} onChange={(v) => setField("current_not_due", v)} />
                </Field>
                <Field label="Due date *" required testid="paye-due-date">
                    <DateInput value={draft.current_due_date} onChange={(v) => setField("current_due_date", v)} testid="paye-due-date-picker" />
                </Field>
            </div>

            <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50/60 text-sky-900 text-[12px] p-2.5 inline-flex items-center gap-2">
                <Lightbulb className="h-3.5 w-3.5" />
                This is the PAYE for the most recent payroll period that is not yet due to IRD.
            </div>

            <div className="mt-4">
                <Field label="Notes (optional)" testid="paye-notes">
                    <Textarea rows={3} value={draft.notes || ""} maxLength={500}
                        onChange={(e) => setField("notes", e.target.value)}
                        placeholder="Client advised they will make a payment next week." />
                    <div className="text-right text-[10.5px] text-[#5E636E] mt-1">{(draft.notes || "").length}/500</div>
                </Field>
            </div>

            <div className="mt-5 flex items-center justify-between">
                <Button variant="outline" className="h-8 text-[12.5px]" data-testid="back-btn">
                    <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
                </Button>
                <Button className="h-9 gap-1 text-[12.5px]" onClick={save} disabled={saving}
                    data-testid="save-next-btn">
                    Save &amp; Next <Check className="h-3.5 w-3.5" />
                </Button>
            </div>
        </section>
    );
}

function PayeSummary({ paye, totals }) {
    const overdue = Number(paye?.overdue || 0);
    const current = Number(paye?.current_not_due || 0);
    const balance = Number(paye?.balance || 0);
    // Still-accruing portion = balance − overdue − current_not_due
    // (assumes `includes_overdue`). Represents payroll for the current
    // month that hasn't reached its due date yet.
    const accruing = paye?.includes_overdue
        ? Math.max(0, balance - overdue - current)
        : 0;
    const total = overdue + current + accruing;
    return (
        <aside className="rounded-xl border border-[#E4E4E7] bg-white p-4 self-start" data-testid="paye-summary">
            <div className="text-[13px] font-semibold text-[#0F1115] mb-3">PAYE Summary</div>
            <ul className="space-y-2 text-[12.5px]">
                <li className="flex justify-between"><span className="text-[#5E636E]">IRD PAYE balance</span><span className="text-[#0F1115] tabular-nums">{nz(balance)}</span></li>
                <li className="flex justify-between"><span className="text-[#5E636E]">Current PAYE (not yet due)</span><span className="text-[#0F1115] tabular-nums">{nz(current)}</span></li>
                {accruing > 0 && (
                    <li className="flex justify-between"><span className="text-[#5E636E]">Still accruing (this month)</span><span className="text-[#0F1115] tabular-nums">{nz(accruing)}</span></li>
                )}
                <li className="flex justify-between border-t border-[#EEEDE7] pt-2"><span className="font-semibold text-[#0F1115]">Total PAYE position</span><span className="font-semibold text-[#0F1115] tabular-nums">{nz(total)}</span></li>
            </ul>
            {overdue > 0 && (
                <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-2.5 flex items-center justify-between text-[12px]">
                    <span className="inline-flex items-center gap-1.5 text-red-700"><span className="h-1.5 w-1.5 rounded-full bg-red-500" /> Overdue amount</span>
                    <span className="font-semibold text-red-700 tabular-nums">{nz(overdue)}</span>
                </div>
            )}
            {current > 0 && (
                <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 flex items-center justify-between text-[12px]">
                    <span className="inline-flex items-center gap-1.5 text-amber-700"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Due soon{paye?.current_due_date ? ` (${new Date(paye.current_due_date).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })})` : ""}</span>
                    <span className="font-semibold text-amber-700 tabular-nums">{nz(current)}</span>
                </div>
            )}
            {accruing > 0 && (
                <div className="mt-2 rounded-lg bg-sky-50 border border-sky-200 p-2.5 text-[11.5px] text-sky-900 leading-relaxed">
                    <b className="font-semibold">Still accruing</b> — this month's payroll liability isn't fully formed yet. It stays in <i>future</i> until the pay period closes, so the client doesn't think it's immediately due.
                </div>
            )}
        </aside>
    );
}

function Field({ label, children, required, testid }) {
    return (
        <div data-testid={testid}>
            <label className="text-[11.5px] font-medium text-[#0F1115] block mb-1.5">
                {label}
            </label>
            {children}
        </div>
    );
}

function CurrencyInput({ value, onChange, testid }) {
    // Plain text input (no browser spinner arrows). Accepts digits, one decimal
    // point, and a leading minus for credits.
    return (
        <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-[#5E636E] font-medium pointer-events-none">NZ$</span>
            <Input type="text" inputMode="decimal" value={value ?? ""}
                data-testid={testid}
                onChange={(e) => {
                    let s = e.target.value;
                    // Keep digits, decimal, minus; collapse to one decimal.
                    s = s.replace(/[^0-9.-]/g, "");
                    const parts = s.split(".");
                    if (parts.length > 2) s = parts[0] + "." + parts.slice(1).join("");
                    s = s.replace(/(.)-+/g, "$1");  // strip stray mid-string minuses
                    onChange(s === "" || s === "-" ? null : s);
                }}
                className="h-9 pl-11 text-right tabular-nums" />
        </div>
    );
}

function DateInput({ value, onChange, testid, placeholder = "Pick a date" }) {
    const d = value ? (() => {
        try { return parseISO(String(value).slice(0, 10)); } catch { return null; }
    })() : null;
    const [open, setOpen] = useState(false);
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button type="button" data-testid={testid}
                    className="h-9 w-full inline-flex items-center justify-between rounded-md border border-input bg-background px-3 text-[13px] text-[#0F1115] hover:bg-zinc-50">
                    <span className={d ? "" : "text-[#9CA3AF]"}>
                        {d ? format(d, "dd MMM yyyy") : placeholder}
                    </span>
                    <CalendarIcon className="h-3.5 w-3.5 text-[#5E636E]" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0">
                <Calendar mode="single" selected={d}
                    onSelect={(dt) => { onChange(dt ? format(dt, "yyyy-MM-dd") : null); setOpen(false); }}
                    initialFocus />
            </PopoverContent>
        </Popover>
    );
}

function PhasePlaceholder({ label }) {
    return (
        <section className="rounded-xl border border-dashed border-[#E4E4E7] bg-white p-8 text-center">
            <AlertCircle className="h-6 w-6 text-[#5E636E] mx-auto" />
            <div className="mt-2 text-[14px] font-semibold text-[#0F1115]">{label} step — coming next phase</div>
            <p className="text-[12.5px] text-[#5E636E] mt-1 max-w-sm mx-auto">Phase 1 ships the PAYE flow end-to-end. The {label} form uses the same pattern and lands in Phase 2.</p>
        </section>
    );
}

// ------- GST step -------------------------------------------------------
function GstStep({ gst, saving, onPatch }) {
    const [draft, setDraft] = useState(gst);
    useEffect(() => { setDraft(gst); }, [gst]);
    function setField(k, v) { setDraft((d) => ({ ...d, [k]: v })); }

    async function save() {
        await onPatch({
            ...draft,
            balance: num(draft.balance),
            overdue: num(draft.overdue),
            next_due_amount: num(draft.next_due_amount),
            verified_at: new Date().toISOString(),
        });
    }

    return (
        <section className="rounded-xl border border-[#E4E4E7] bg-white p-5" data-testid="gst-step">
            <div className="flex items-start justify-between mb-1">
                <div>
                    <h2 className="text-[15px] font-semibold text-[#0F1115]">GST</h2>
                    <p className="text-[12.5px] text-[#5E636E] mt-0.5">Enter the current IRD GST account position and the next GST return amount / due date. Tick <i>No GST balance</i> if myIR shows a clean account.</p>
                </div>
                <a href="https://www.ird.govt.nz/" target="_blank" rel="noreferrer"
                    className="shrink-0 inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white px-2.5 py-1 text-[11.5px] text-[#5E636E] hover:bg-zinc-50 whitespace-nowrap">
                    <Lightbulb className="h-3 w-3" /> myIR help <ExternalLink className="h-3 w-3" />
                </a>
            </div>

            <label className="inline-flex items-center gap-2 mt-3 text-[12.5px] text-[#0F1115]">
                <input type="checkbox" checked={!!draft.verified_zero}
                    onChange={(e) => {
                        const v = e.target.checked;
                        setDraft((d) => ({ ...d, verified_zero: v, balance: v ? 0 : d.balance, overdue: v ? 0 : d.overdue }));
                    }}
                    data-testid="gst-verified-zero" />
                No GST balance according to myIR (records a verified zero)
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <Field label="Current IRD GST account balance *" testid="gst-balance">
                    <CurrencyInput value={draft.balance} onChange={(v) => setField("balance", v)} />
                    <label className="flex items-center gap-2 mt-2 text-[12.5px] text-[#5E636E]">
                        <input type="checkbox" checked={!!draft.includes_overdue}
                            onChange={(e) => setField("includes_overdue", e.target.checked)} />
                        Includes overdue amount
                    </label>
                </Field>
                <Field label="Overdue portion (if known)" testid="gst-overdue">
                    <CurrencyInput value={draft.overdue} onChange={(v) => setField("overdue", v)} />
                </Field>
                <Field label="Current GST return period — start" testid="gst-period-start">
                    <DateInput value={draft.period_start} onChange={(v) => setField("period_start", v)} testid="gst-period-start-picker" />
                </Field>
                <Field label="Current GST return period — end" testid="gst-period-end">
                    <DateInput value={draft.period_end} onChange={(v) => setField("period_end", v)} testid="gst-period-end-picker" />
                </Field>
                <Field label="Return filing status" testid="gst-return-status">
                    <select value={draft.return_status || ""} onChange={(e) => setField("return_status", e.target.value)}
                        className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-[13px]">
                        <option value="">Select…</option>
                        <option value="not_started">Not started</option>
                        <option value="in_progress">In progress</option>
                        <option value="filed">Filed</option>
                        <option value="not_required">Not required</option>
                        <option value="unknown">Unknown</option>
                    </select>
                </Field>
                <Field label="Source" testid="gst-source">
                    <SourceSelect value={draft.source} onChange={(v) => setField("source", v)} />
                </Field>
                <Field label="Next GST amount due" testid="gst-next-amount">
                    <CurrencyInput value={draft.next_due_amount} onChange={(v) => setField("next_due_amount", v)} />
                </Field>
                <Field label="Next GST due date" testid="gst-next-date">
                    <DateInput value={draft.next_due_date} onChange={(v) => setField("next_due_date", v)} testid="gst-next-date-picker" />
                </Field>
            </div>

            <div className="mt-4">
                <Field label="Notes (optional)" testid="gst-notes">
                    <Textarea rows={3} value={draft.notes || ""} maxLength={500}
                        onChange={(e) => setField("notes", e.target.value)}
                        placeholder="e.g. Return has been filed, client paying on due date." />
                    <div className="text-right text-[10.5px] text-[#5E636E] mt-1">{(draft.notes || "").length}/500</div>
                </Field>
            </div>

            <div className="mt-5 flex items-center justify-between">
                <Button variant="outline" className="h-8 text-[12.5px]">
                    <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
                </Button>
                <Button className="h-9 gap-1 text-[12.5px]" onClick={save} disabled={saving}
                    data-testid="gst-save-next">
                    Save &amp; Next <Check className="h-3.5 w-3.5" />
                </Button>
            </div>
        </section>
    );
}

function GstSummary({ gst }) {
    const balance = num(gst.balance) || 0;
    const overdue = num(gst.overdue) || 0;
    const nextAmount = num(gst.next_due_amount) || 0;
    const nonOverdue = gst.includes_overdue ? Math.max(0, balance - overdue) : balance;
    const total = overdue + nonOverdue + nextAmount;
    return (
        <aside className="rounded-xl border border-[#E4E4E7] bg-white p-4 self-start" data-testid="gst-summary">
            <div className="text-[13px] font-semibold text-[#0F1115] mb-3">GST Summary</div>
            <ul className="space-y-2 text-[12.5px]">
                <li className="flex justify-between"><span className="text-[#5E636E]">IRD GST balance</span><span className="tabular-nums">{nz(balance)}</span></li>
                <li className="flex justify-between"><span className="text-[#5E636E]">Next return amount</span><span className="tabular-nums">{nz(nextAmount)}</span></li>
                <li className="flex justify-between border-t border-[#EEEDE7] pt-2"><span className="font-semibold text-[#0F1115]">Total GST position</span><span className="font-semibold tabular-nums">{nz(total)}</span></li>
            </ul>
            {overdue > 0 && (
                <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-2.5 flex items-center justify-between text-[12px]">
                    <span className="inline-flex items-center gap-1.5 text-red-700"><span className="h-1.5 w-1.5 rounded-full bg-red-500" /> Overdue</span>
                    <span className="font-semibold text-red-700 tabular-nums">{nz(overdue)}</span>
                </div>
            )}
            {nextAmount > 0 && gst.next_due_date && (
                <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 flex items-center justify-between text-[12px]">
                    <span className="inline-flex items-center gap-1.5 text-amber-700"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Next due {new Date(gst.next_due_date).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}</span>
                    <span className="font-semibold text-amber-700 tabular-nums">{nz(nextAmount)}</span>
                </div>
            )}
        </aside>
    );
}

// ------- Income Tax step ------------------------------------------------
function IncomeTaxStep({ it, saving, onPatch }) {
    const [draft, setDraft] = useState(it);
    useEffect(() => { setDraft(it); }, [it]);
    function setField(k, v) { setDraft((d) => ({ ...d, [k]: v })); }

    const insts = Array.isArray(draft.provisional_instalments) ? draft.provisional_instalments : [];
    function patchInstalment(i, patch) {
        const next = insts.map((row, idx) => idx === i ? { ...row, ...patch } : row);
        setField("provisional_instalments", next);
    }
    function addInstalment() {
        setField("provisional_instalments", [...insts, {
            tax_year: "", name: "", method: "standard",
            amount: null, due_date: "", status: "upcoming", source: "",
        }]);
    }
    function removeInstalment(i) {
        setField("provisional_instalments", insts.filter((_, idx) => idx !== i));
    }

    async function save() {
        await onPatch({
            ...draft,
            balance: num(draft.balance),
            overdue: num(draft.overdue),
            penalties_interest: num(draft.penalties_interest),
            estimated_current_year: num(draft.estimated_current_year),
            estimated_paid: num(draft.estimated_paid),
            estimated_remaining: num(draft.estimated_remaining),
            provisional_instalments: insts.map((row) => ({
                ...row,
                amount: num(row.amount),
                amount_paid: num(row.amount_paid),
                balance_remaining: num(row.balance_remaining),
            })),
            verified_at: new Date().toISOString(),
        });
    }

    return (
        <section className="rounded-xl border border-[#E4E4E7] bg-white p-5 space-y-6" data-testid="income-tax-step">
            <div className="flex items-start justify-between">
                <div>
                    <h2 className="text-[15px] font-semibold text-[#0F1115]">Income Tax &amp; Provisional Tax</h2>
                    <p className="text-[12.5px] text-[#5E636E] mt-0.5">Record current income tax position, upcoming provisional tax instalments, and any estimated income tax reserve.</p>
                </div>
                <a href="https://www.ird.govt.nz/" target="_blank" rel="noreferrer"
                    className="shrink-0 inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white px-2.5 py-1 text-[11.5px] text-[#5E636E] hover:bg-zinc-50 whitespace-nowrap">
                    <Lightbulb className="h-3 w-3" /> myIR help <ExternalLink className="h-3 w-3" />
                </a>
            </div>

            {/* A. Current Income Tax Position */}
            <div>
                <h3 className="text-[12.5px] font-semibold uppercase tracking-wider text-[#5E636E] mb-3">A. Current income tax position</h3>
                <label className="inline-flex items-center gap-2 mb-3 text-[12.5px] text-[#0F1115]">
                    <input type="checkbox" checked={!!draft.verified_zero}
                        onChange={(e) => {
                            const v = e.target.checked;
                            setDraft((d) => ({ ...d, verified_zero: v, balance: v ? 0 : d.balance, overdue: v ? 0 : d.overdue }));
                        }}
                        data-testid="it-verified-zero" />
                    No income tax balance according to myIR (records a verified zero)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Income tax account balance" testid="it-balance">
                        <CurrencyInput value={draft.balance} onChange={(v) => setField("balance", v)} />
                    </Field>
                    <Field label="Overdue portion" testid="it-overdue">
                        <CurrencyInput value={draft.overdue} onChange={(v) => setField("overdue", v)} />
                    </Field>
                    <Field label="Tax year" testid="it-tax-year">
                        <Input value={draft.tax_year || ""} onChange={(e) => setField("tax_year", e.target.value)}
                            placeholder="e.g. 2026" className="h-9" />
                    </Field>
                    <Field label="Original due date" testid="it-due-date">
                        <DateInput value={draft.original_due_date} onChange={(v) => setField("original_due_date", v)} testid="it-due-date-picker" />
                    </Field>
                    <Field label="Penalties / interest (if separate)" testid="it-penalties">
                        <CurrencyInput value={draft.penalties_interest} onChange={(v) => setField("penalties_interest", v)} />
                    </Field>
                    <Field label="Source" testid="it-source">
                        <SourceSelect value={draft.source} onChange={(v) => setField("source", v)} />
                    </Field>
                </div>
            </div>

            {/* B. Provisional Tax Instalments */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-[12.5px] font-semibold uppercase tracking-wider text-[#5E636E]">B. Provisional tax instalments</h3>
                    <Button variant="outline" className="h-7 text-[11.5px]" onClick={addInstalment}
                        data-testid="add-prov-instalment">+ Add instalment</Button>
                </div>
                {!insts.length && (
                    <div className="rounded-md border border-dashed border-[#E4E4E7] p-4 text-center text-[12.5px] text-[#5E636E]">
                        No provisional tax instalments recorded. Click + Add instalment to enter P1 / P2 / P3.
                    </div>
                )}
                {insts.map((row, i) => (
                    <div key={i} className="mb-3 rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] p-3"
                        data-testid={`prov-inst-${i}`}>
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
                            <div className="col-span-1">
                                <label className="text-[10.5px] font-medium text-[#5E636E]">Year</label>
                                <Input value={row.tax_year || ""} onChange={(e) => patchInstalment(i, { tax_year: e.target.value })}
                                    placeholder="2027" className="h-8 text-[12px]" />
                            </div>
                            <div className="col-span-1">
                                <label className="text-[10.5px] font-medium text-[#5E636E]">Name</label>
                                <Input value={row.name || ""} onChange={(e) => patchInstalment(i, { name: e.target.value })}
                                    placeholder="P1" className="h-8 text-[12px]" />
                            </div>
                            <div className="col-span-1">
                                <label className="text-[10.5px] font-medium text-[#5E636E]">Method</label>
                                <select value={row.method || "standard"}
                                    onChange={(e) => patchInstalment(i, { method: e.target.value })}
                                    className="h-8 w-full rounded-md border border-input bg-background px-2 text-[12px]">
                                    <option value="standard">Standard</option>
                                    <option value="estimation">Estimation</option>
                                    <option value="aim">AIM</option>
                                    <option value="ratio">Ratio</option>
                                    <option value="unknown">Unknown</option>
                                </select>
                            </div>
                            <div className="col-span-1">
                                <label className="text-[10.5px] font-medium text-[#5E636E]">Amount</label>
                                <CurrencyInput value={row.amount} onChange={(v) => patchInstalment(i, { amount: v, balance_remaining: row.balance_remaining ?? v })} />
                            </div>
                            <div className="col-span-1">
                                <label className="text-[10.5px] font-medium text-[#5E636E]">Due date</label>
                                <DateInput value={row.due_date}
                                    onChange={(v) => patchInstalment(i, { due_date: v })}
                                    testid={`prov-inst-${i}-due-picker`} />
                            </div>
                            <div className="col-span-1">
                                <label className="text-[10.5px] font-medium text-[#5E636E]">Status</label>
                                <select value={row.status || "upcoming"}
                                    onChange={(e) => patchInstalment(i, { status: e.target.value })}
                                    className="h-8 w-full rounded-md border border-input bg-background px-2 text-[12px]">
                                    <option value="upcoming">Upcoming</option>
                                    <option value="unpaid">Unpaid</option>
                                    <option value="part-paid">Part-paid</option>
                                    <option value="paid">Paid</option>
                                    <option value="overdue">Overdue</option>
                                    <option value="superseded">Superseded</option>
                                    <option value="not_required">Not required</option>
                                </select>
                            </div>
                            <div className="col-span-1">
                                <label className="text-[10.5px] font-medium text-[#5E636E]">Amount paid</label>
                                <CurrencyInput value={row.amount_paid} onChange={(v) => patchInstalment(i, { amount_paid: v, balance_remaining: Math.max(0, (num(row.amount) || 0) - (num(v) || 0)) })} />
                            </div>
                            <div className="col-span-1">
                                <label className="text-[10.5px] font-medium text-[#5E636E]">Balance left</label>
                                <CurrencyInput value={row.balance_remaining} onChange={(v) => patchInstalment(i, { balance_remaining: v })} />
                            </div>
                            <div className="col-span-3 flex items-end gap-2">
                                <div className="flex-1">
                                    <label className="text-[10.5px] font-medium text-[#5E636E]">Notes</label>
                                    <Input value={row.notes || ""} onChange={(e) => patchInstalment(i, { notes: e.target.value })}
                                        className="h-8 text-[12px]" />
                                </div>
                                <Button variant="outline" className="h-8 text-[11px] text-red-600 border-red-200 hover:bg-red-50"
                                    onClick={() => removeInstalment(i)} data-testid={`remove-prov-${i}`}>
                                    Remove
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* C. Estimated Income Tax */}
            <div>
                <h3 className="text-[12.5px] font-semibold uppercase tracking-wider text-[#5E636E] mb-3">C. Estimated income tax (optional)</h3>
                <p className="text-[11.5px] text-[#5E636E] mb-3 italic">Estimated amounts are shown to the client as "future / estimated — not yet confirmed as payable to IRD".</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Estimated current-year income tax" testid="it-est-year">
                        <CurrencyInput value={draft.estimated_current_year} onChange={(v) => setField("estimated_current_year", v)} />
                    </Field>
                    <Field label="Tax already paid / credited" testid="it-est-paid">
                        <CurrencyInput value={draft.estimated_paid} onChange={(v) => setField("estimated_paid", v)} />
                    </Field>
                    <Field label="Remaining estimated tax (shown to client)" testid="it-est-remaining">
                        <CurrencyInput value={draft.estimated_remaining} onChange={(v) => setField("estimated_remaining", v)} />
                    </Field>
                    <Field label="Confidence" testid="it-est-conf">
                        <select value={draft.estimated_confidence || "draft"}
                            onChange={(e) => setField("estimated_confidence", e.target.value)}
                            className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-[13px]">
                            <option value="draft">Draft estimate</option>
                            <option value="staff_reviewed">Staff reviewed</option>
                            <option value="approved">Approved</option>
                            <option value="superseded">Superseded</option>
                        </select>
                    </Field>
                </div>
            </div>

            <div>
                <Field label="Notes (optional)" testid="it-notes">
                    <Textarea rows={3} value={draft.notes || ""} maxLength={500}
                        onChange={(e) => setField("notes", e.target.value)} />
                </Field>
            </div>

            <div className="flex items-center justify-between">
                <Button variant="outline" className="h-8 text-[12.5px]">
                    <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
                </Button>
                <Button className="h-9 gap-1 text-[12.5px]" onClick={save} disabled={saving}
                    data-testid="it-save-next">
                    Save &amp; Next <Check className="h-3.5 w-3.5" />
                </Button>
            </div>
        </section>
    );
}

function IncomeTaxSummary({ it }) {
    const balance = num(it.balance) || 0;
    const overdue = num(it.overdue) || 0;
    const estRemaining = num(it.estimated_remaining) || 0;
    const insts = Array.isArray(it.provisional_instalments) ? it.provisional_instalments : [];
    const instTotal = insts
        .filter((row) => !["paid", "superseded", "not_required", "cancelled"].includes((row.status || "").toLowerCase()))
        .reduce((s, row) => s + (num(row.balance_remaining ?? row.amount) || 0), 0);
    return (
        <aside className="rounded-xl border border-[#E4E4E7] bg-white p-4 self-start" data-testid="income-tax-summary">
            <div className="text-[13px] font-semibold text-[#0F1115] mb-3">Income Tax Summary</div>
            <ul className="space-y-2 text-[12.5px]">
                <li className="flex justify-between"><span className="text-[#5E636E]">IRD income tax balance</span><span className="tabular-nums">{nz(balance)}</span></li>
                <li className="flex justify-between"><span className="text-[#5E636E]">Provisional tax outstanding</span><span className="tabular-nums">{nz(instTotal)}</span></li>
                <li className="flex justify-between"><span className="text-[#5E636E]">Estimated remaining</span><span className="tabular-nums">{nz(estRemaining)}</span></li>
                <li className="flex justify-between border-t border-[#EEEDE7] pt-2"><span className="font-semibold text-[#0F1115]">Total income tax position</span><span className="font-semibold tabular-nums">{nz(balance + instTotal + estRemaining)}</span></li>
            </ul>
            {overdue > 0 && (
                <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-2.5 flex items-center justify-between text-[12px]">
                    <span className="inline-flex items-center gap-1.5 text-red-700"><span className="h-1.5 w-1.5 rounded-full bg-red-500" /> Overdue</span>
                    <span className="font-semibold text-red-700 tabular-nums">{nz(overdue)}</span>
                </div>
            )}
        </aside>
    );
}

function SourceSelect({ value, onChange }) {
    return (
        <select value={value || ""} onChange={(e) => onChange(e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-[13px]">
            <option value="">Select…</option>
            <option value="myir_verified">myIR verified</option>
            <option value="xero">Xero</option>
            <option value="xero_payroll">Xero Payroll</option>
            <option value="xero_tax">Xero Tax / Practice Manager</option>
            <option value="clientops_calculation">ClientOps calculation</option>
            <option value="client_supplied">Client supplied</option>
            <option value="staff_adjustment">Staff adjustment</option>
            <option value="other">Other</option>
        </select>
    );
}

function num(v) {
    if (v === "" || v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

// -------- Send Report dialog (editable on-screen preview) ---------------
function SendReportDialog({ open, onOpenChange, clientId, clientName, totals }) {
    const [draft, setDraft] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [include, setInclude] = useState({ gst: true, paye: true, income_tax: true });
    const [bodyEdited, setBodyEdited] = useState(false);

    async function fetchCompose(flags) {
        setLoading(true);
        try {
            const { data } = await api.get(`/tax-position/clients/${clientId}/compose`, {
                params: {
                    include_gst: flags.gst ? "true" : "false",
                    include_paye: flags.paye ? "true" : "false",
                    include_income_tax: flags.income_tax ? "true" : "false",
                },
            });
            setDraft((prev) => ({
                ...data,
                // Preserve the user's To field if they already changed it; always refresh subject/body.
                to: prev?.to || data.to,
                body: data.body,
                subject: data.subject,
            }));
            setBodyEdited(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to compose");
        } finally { setLoading(false); }
    }

    useEffect(() => {
        if (!open) { setDraft(null); setBodyEdited(false); setInclude({ gst: true, paye: true, income_tax: true }); return; }
        fetchCompose({ gst: true, paye: true, income_tax: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, clientId]);

    function toggle(key) {
        const next = { ...include, [key]: !include[key] };
        // Need at least one account ticked.
        if (!next.gst && !next.paye && !next.income_tax) {
            toast.error("Tick at least one tax account to include");
            return;
        }
        setInclude(next);
        // Only refetch if the user hasn't edited the body manually.
        if (!bodyEdited) fetchCompose(next);
        else toast.message("Body has unsaved edits — toggle will take effect after you reset", {
            action: { label: "Reset body", onClick: () => fetchCompose(next) },
        });
    }

    async function send() {
        if (!draft?.to) { toast.error("Add a recipient email first"); return; }
        try {
            setSending(true);
            await api.post(`/tax-position/clients/${clientId}/send-report`, {
                to: draft.to, subject: draft.subject, body: draft.body, cc: [],
            });
            toast.success(`Sent to ${draft.to}`);
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        } finally { setSending(false); }
    }

    const sections = draft?.sections || {};
    const sectionLabel = { gst: "GST", paye: "PAYE", income_tax: "Income tax" };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Send tax position to {clientName}</DialogTitle>
                </DialogHeader>
                {loading || !draft ? (
                    <div className="py-8 text-center text-[13px] text-[#5E636E]">
                        <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Preparing preview…
                    </div>
                ) : (
                    <div className="space-y-3 text-[13px]">
                        <div>
                            <div className="text-[11.5px] font-medium text-[#5E636E] mb-1.5">Include which accounts?</div>
                            <div className="flex flex-wrap gap-2">
                                {["gst", "paye", "income_tax"].map((k) => {
                                    const on = include[k];
                                    const sec = sections[k];
                                    const total = sec ? (Number(sec.overdue_total) + Number(sec.due_soon_total) + Number(sec.future_total)) : 0;
                                    return (
                                        <button key={k} type="button" onClick={() => toggle(k)}
                                            data-testid={`include-${k}`}
                                            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12px] transition ${on ? "bg-[#0F1115] text-white border-[#0F1115]" : "bg-white text-[#5E636E] border-[#E4E4E7] hover:bg-zinc-50"}`}>
                                            <span className={`h-3.5 w-3.5 rounded-sm flex items-center justify-center ${on ? "bg-white" : "border border-[#E4E4E7]"}`}>
                                                {on ? <Check className="h-2.5 w-2.5 text-[#0F1115]" /> : null}
                                            </span>
                                            <span className="font-medium">{sectionLabel[k]}</span>
                                            {sec && <span className="tabular-nums opacity-80">{nz(total)}</span>}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[11px] text-[#5E636E] mt-1.5">Only the ticked accounts show in the email body and totals the client sees.</p>
                        </div>
                        <div>
                            <label className="block text-[11.5px] font-medium text-[#5E636E] mb-1">To</label>
                            <Input value={draft.to || ""} onChange={(e) => setDraft({ ...draft, to: e.target.value })}
                                placeholder="client@example.com" data-testid="send-to-input" />
                        </div>
                        <div>
                            <label className="block text-[11.5px] font-medium text-[#5E636E] mb-1">Subject</label>
                            <Input value={draft.subject || ""} onChange={(e) => { setDraft({ ...draft, subject: e.target.value }); setBodyEdited(true); }}
                                data-testid="send-subject-input" />
                        </div>
                        <div>
                            <label className="block text-[11.5px] font-medium text-[#5E636E] mb-1">Message</label>
                            <Textarea rows={14} value={draft.body || ""}
                                onChange={(e) => { setDraft({ ...draft, body: e.target.value }); setBodyEdited(true); }}
                                data-testid="send-body-input"
                                className="font-mono text-[12.5px] leading-relaxed" />
                            <div className="text-right text-[10.5px] text-[#5E636E] mt-1">Edit anything above before you hit Send.</div>
                        </div>
                        <div className="rounded-md border border-[#E4E4E7] bg-[#FAFAFA] p-3 text-[11.5px] text-[#5E636E] grid grid-cols-4 gap-2">
                            <Stat label="Overdue"   amount={draft.totals?.overdue_total}   tone="red" />
                            <Stat label="Due soon"  amount={draft.totals?.due_soon_total}  tone="amber" />
                            <Stat label="Future"    amount={draft.totals?.future_total}    tone="sky" />
                            <Stat label="Total"     amount={draft.totals?.grand_total}     tone="black" />
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={send} disabled={!draft || sending} data-testid="confirm-send-btn">
                        {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                        Send email
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Stat({ label, amount, tone }) {
    const color = tone === "red" ? "text-red-600" : tone === "amber" ? "text-amber-600" : tone === "sky" ? "text-sky-600" : "text-[#0F1115]";
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider">{label}</div>
            <div className={`text-[13px] font-semibold tabular-nums ${color}`}>{nz(amount)}</div>
        </div>
    );
}

// -------- Paste from myIR dialog ---------------------------------------
function PasteMyirDialog({ open, onOpenChange, onApply }) {
    const [text, setText] = useState("");
    const [parsing, setParsing] = useState(false);
    const [parsed, setParsed] = useState(null);

    useEffect(() => { if (!open) { setText(""); setParsed(null); } }, [open]);

    async function parse() {
        try {
            setParsing(true);
            const { data } = await api.post("/tax-position/parse-myir", { text });
            setParsed(data);
            if (!data.found?.length) toast.warning("Nothing recognised — check the paste or adjust manually.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Parse failed");
        } finally { setParsing(false); }
    }

    async function apply() {
        if (!parsed) return;
        await onApply(parsed);
        onOpenChange(false);
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle className="inline-flex items-center gap-2">
                        <ClipboardPaste className="h-4 w-4" /> Paste from myIR
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <p className="text-[12.5px] text-[#5E636E]">
                        Open the client in myIR, press <kbd className="rounded border border-[#E4E4E7] bg-zinc-50 px-1 text-[11px]">Ctrl</kbd>+<kbd className="rounded border border-[#E4E4E7] bg-zinc-50 px-1 text-[11px]">A</kbd> → <kbd className="rounded border border-[#E4E4E7] bg-zinc-50 px-1 text-[11px]">Ctrl</kbd>+<kbd className="rounded border border-[#E4E4E7] bg-zinc-50 px-1 text-[11px]">C</kbd>, paste below. We detect <b>GST</b>, <b>Payroll (PAYE)</b> and <b>Income tax</b> totals, due-now amounts, period rows, and provisional tax.
                    </p>
                    <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)}
                        className="font-mono text-[12px] leading-relaxed"
                        placeholder={`Example:\n\nGST\nTotal balance: $16,600.97\nDue now: $0.00\n\nIncome tax\n2027 Provisional tax: $31,881.92\nTotal balance: -$1,433.32\n\nPayroll\n31-Oct-2026\nTotal balance: $1,954.19\nDue now: $804.47`}
                        data-testid="myir-paste-text" />
                    <div className="flex items-center gap-2">
                        <Button onClick={parse} disabled={!text.trim() || parsing} data-testid="myir-parse-btn">
                            {parsing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                            Detect fields
                        </Button>
                        {parsed && (
                            <span className="text-[12px] text-[#5E636E]">
                                Found {parsed.found?.length || 0} item{(parsed.found?.length || 0) === 1 ? "" : "s"}.
                            </span>
                        )}
                    </div>
                    {parsed?.found?.length ? (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3" data-testid="myir-preview-box">
                            <div className="text-[12px] font-semibold text-emerald-900 mb-2">Preview of what we'll populate</div>
                            <ul className="text-[12.5px] text-emerald-900 space-y-1">
                                {parsed.found.map((f, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <Check className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {f}
                                    </li>
                                ))}
                            </ul>
                            {Array.isArray(parsed.paye_rows_preview) && parsed.paye_rows_preview.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-emerald-200" data-testid="paye-rows-preview">
                                    <div className="text-[11px] uppercase tracking-wider text-emerald-900 font-semibold mb-1.5">PAYE period rows</div>
                                    <table className="w-full text-[12px] bg-white rounded-md border border-emerald-200 overflow-hidden">
                                        <thead>
                                            <tr className="text-left text-[10.5px] uppercase tracking-wider text-[#5E636E] bg-emerald-50/50">
                                                <th className="px-2 py-1.5">Period</th>
                                                <th className="px-2 py-1.5 text-right">Amount</th>
                                                <th className="px-2 py-1.5">Due</th>
                                                <th className="px-2 py-1.5">Bucket</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {parsed.paye_rows_preview.map((r, i) => {
                                                const pill = r.bucket === "overdue"
                                                    ? { label: "Overdue", cls: "bg-red-50 text-red-700 border-red-200", icon: "●" }
                                                    : r.bucket === "due_soon"
                                                        ? { label: "Due soon", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: "●" }
                                                        : { label: "Still accruing", cls: "bg-sky-50 text-sky-700 border-sky-200", icon: "●" };
                                                return (
                                                    <tr key={i} className="border-t border-emerald-100"
                                                        data-testid={`paye-row-${i}`}>
                                                        <td className="px-2 py-1.5 text-[#0F1115]">{r.period}</td>
                                                        <td className="px-2 py-1.5 text-right tabular-nums text-[#0F1115]">{nz(r.amount)}</td>
                                                        <td className="px-2 py-1.5 text-[#5E636E]">
                                                            {r.due_date ? new Date(r.due_date).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                                                        </td>
                                                        <td className="px-2 py-1.5">
                                                            <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10.5px] font-medium ${pill.cls}`}>
                                                                <span className="text-[8px] leading-none">{pill.icon}</span>
                                                                {pill.label}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <p className="text-[11px] text-emerald-800 mt-2">Nothing is saved until you click Apply. You can still edit each step after.</p>
                        </div>
                    ) : null}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={apply} disabled={!parsed?.found?.length} data-testid="myir-apply-btn">
                        Apply to form
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ReviewStep({ totals, client, onFinalise, saving }) {
    return (
        <section className="rounded-xl border border-[#E4E4E7] bg-white p-5">
            <h2 className="text-[15px] font-semibold text-[#0F1115]">Review &amp; Save</h2>
            <ul className="mt-4 space-y-2 text-[13px]">
                <li className="flex justify-between border-b border-[#EEEDE7] pb-2"><span className="text-[#5E636E]">Total overdue</span><span className="font-semibold text-red-600 tabular-nums">{nz(totals.overdue_total)}</span></li>
                <li className="flex justify-between border-b border-[#EEEDE7] pb-2"><span className="text-[#5E636E]">Total due soon</span><span className="font-semibold text-amber-600 tabular-nums">{nz(totals.due_soon_total)}</span></li>
                <li className="flex justify-between border-b border-[#EEEDE7] pb-2"><span className="text-[#5E636E]">Total future / estimated</span><span className="font-semibold text-sky-600 tabular-nums">{nz(totals.future_total)}</span></li>
                <li className="flex justify-between pt-2"><span className="font-semibold text-[#0F1115]">Total tax position</span><span className="font-bold text-[#0F1115] tabular-nums">{nz(totals.grand_total)}</span></li>
            </ul>
            <label className="flex items-start gap-2 mt-5 text-[12.5px] text-[#5E636E]">
                <input type="checkbox" className="mt-0.5" />
                I confirm that I have checked the relevant client tax accounts and the information entered reflects the information available as at the verification date.
            </label>
            <div className="mt-5 flex justify-end">
                <Button onClick={onFinalise} disabled={saving} className="h-9 gap-1 text-[12.5px]">
                    <Save className="h-3.5 w-3.5" /> Save Snapshot
                </Button>
            </div>
        </section>
    );
}

function ClientReportPreview({ totals, client, onSend }) {
    const asOf = new Date().toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" });
    return (
        <section className="rounded-xl border border-[#E4E4E7] bg-white p-5" data-testid="client-report-preview">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[12.5px] font-semibold text-[#0F1115]">Client Report Preview</div>
                <Button className="h-7 text-[11.5px]" onClick={onSend} data-testid="send-to-client-btn">Send to client</Button>
            </div>
            <div>
                <div className="text-[11.5px] text-[#5E636E] uppercase tracking-wider">Your Tax Position</div>
                <div className="text-[16px] font-bold text-[#0F1115] mt-0.5 leading-tight">{client.name}</div>
                <div className="text-[11.5px] text-[#5E636E]">As at {asOf}</div>
            </div>
            <div className="mt-4 space-y-2.5 text-[13px]">
                <Card color="red"   amount={totals.overdue_total}  big="overdue"  caption="This needs attention." />
                <Card color="amber" amount={totals.due_soon_total} big="due soon" caption="Keep this money available for IRD." />
                <Card color="sky"   amount={totals.future_total}   big="future/estimated tax" caption="Plan to keep this aside." />
            </div>
            <div className="mt-4 rounded-xl bg-zinc-50 border border-[#E4E4E7] p-3">
                <div className="text-[11.5px] text-[#5E636E] uppercase tracking-wider">Total tax position</div>
                <div className="text-[24px] font-bold text-[#0F1115] tabular-nums leading-none mt-1">{nz(totals.grand_total)}</div>
            </div>
            <div className="mt-4 rounded-lg bg-sky-50 border border-sky-200 p-3 text-[11.5px] text-sky-900 leading-relaxed">
                Tax shown as upcoming or estimated may not yet be payable to IRD. It is shown so you can plan ahead and avoid spending money that may be needed for tax.
            </div>
        </section>
    );
}

function Card({ color, amount, big, caption }) {
    const palette = {
        red:   { bg: "bg-red-50/60",   dot: "bg-red-500",    label: "text-red-700",    chip: "bg-red-100" },
        amber: { bg: "bg-amber-50/60", dot: "bg-amber-500",  label: "text-amber-700",  chip: "bg-amber-100" },
        sky:   { bg: "bg-sky-50/60",   dot: "bg-sky-500",    label: "text-sky-700",    chip: "bg-sky-100" },
    }[color];
    return (
        <div className={`rounded-xl ${palette.bg} border border-[#E4E4E7] p-3`}>
            <div className="flex items-start gap-2.5">
                <span className={`mt-0.5 h-6 w-6 shrink-0 rounded-full ${palette.chip} ${palette.label} flex items-center justify-center`}>
                    <span className={`h-2 w-2 rounded-full ${palette.dot}`} />
                </span>
                <div>
                    <div className={`text-[14px] font-semibold ${palette.label} tabular-nums`}>
                        {nz(amount)} <span className="font-normal">{big}</span>
                    </div>
                    <div className="text-[11.5px] text-[#5E636E]">{caption}</div>
                </div>
            </div>
        </div>
    );
}
